import {
  useMemo,
  useState,
  type FormEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import {  useQuery } from 'convex/react';
import { api } from '@repo/backend';
import { AppButton } from '@repo/ui';
import { FieldLabel, IronDivider, LivePulse, SectionHeader } from '@/components/ui';
import { useSubmitCardListing, type SubmitCardListingPayload } from '@/hooks/useSubmitCardListing';

const rarities = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const;
const cardTypes = ['creature', 'spell', 'artifact', 'enchantment', 'instant', 'land'] as const;

type Rarity = (typeof rarities)[number];
type CardType = (typeof cardTypes)[number];
type SellerIdentity = { name: string; email: string } | null | undefined;

type FormValues = {
  sellerName: string;
  sellerEmail: string;
  name: string;
  description: string;
  flavorText: string;
  rarity: Rarity;
  cardType: CardType;
  manaCost: string;
  power: string;
  toughness: string;
  imageUrl: string;
  startingPrice: string;
  reservePrice: string;
  startsAt: string;
  endsAt: string;
};

type BuildPayloadResult = { error: string } | { payload: SubmitCardListingPayload };

function createInitialForm(): FormValues {
  const startsAt = new Date(Date.now());
  const endsAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  return {
    sellerName: '',
    sellerEmail: '',
    name: '',
    description: '',
    flavorText: '',
    rarity: 'rare',
    cardType: 'creature',
    manaCost: '',
    power: '',
    toughness: '',
    imageUrl: '',
    startingPrice: '',
    reservePrice: '',
    startsAt: toDatetimeLocalValue(startsAt),
    endsAt: toDatetimeLocalValue(endsAt),
  };
}

export const Route = createFileRoute('/cards/new')({
  component: SubmitTradingCardPage,
});

function SubmitTradingCardPage() {
  const currentUser = useQuery(api.users.queries.me);
  const [form, setForm] = useState<FormValues>(() => createInitialForm());
  const [clientError, setClientError] = useState<string | null>(null);

  const { submitListing, reset, state } = useSubmitCardListing();

  const imagePreviewUrl = isValidUrl(form.imageUrl.trim()) ? form.imageUrl.trim() : null;
  const showCreatureStats = form.cardType === 'creature';
  const showManaCost = form.cardType !== 'land';
  const isAuthStateLoading = currentUser === undefined;
  const startsAtLabel = useMemo(() => formatDatePreview(form.startsAt), [form.startsAt]);
  const endsAtLabel = useMemo(() => formatDatePreview(form.endsAt), [form.endsAt]);

  function updateField<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setClientError(null);
    if (state.status !== 'idle') reset();

    setForm((current) => {
      if (key === 'cardType') {
        const nextType = value as CardType;
        return {
          ...current,
          cardType: nextType,
          manaCost: nextType === 'land' ? '' : current.manaCost,
          power: nextType === 'creature' ? current.power : '',
          toughness: nextType === 'creature' ? current.toughness : '',
        };
      }

      return { ...current, [key]: value };
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isAuthStateLoading) {
      setClientError('Please wait while we confirm whether to use your account details.');
      return;
    }

    const validation = buildPayload(
      form,
      currentUser ? { name: currentUser.name, email: currentUser.email } : currentUser,
    );
    if ('error' in validation) {
      setClientError(validation.error);
      return;
    }

    try {
      await submitListing(validation.payload);
      setClientError(null);
      setForm(createInitialForm());
    } catch {
      // Hook state already contains the surfaced error message.
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-iron-800 bg-[radial-gradient(circle_at_top,_rgba(241,198,41,0.15),_transparent_35%),linear-gradient(180deg,_rgba(19,23,31,0.96),_rgba(19,23,31,0.82))]">
        <div className="container mx-auto flex max-w-6xl flex-col gap-6 px-8 py-16">
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/"
              className="text-[10px] font-bold uppercase tracking-[0.32em] text-iron-500 transition-colors hover:text-secondary"
            >
              Auction Hall
            </Link>
            <span className="text-iron-700">/</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.32em] text-secondary">
              Submit Listing
            </span>
          </div>

          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_380px] lg:items-start">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <LivePulse variant="gold" size="md" />
                <p className="text-[10px] font-bold uppercase tracking-[0.38em] text-secondary">
                  Public Auction Intake
                </p>
              </div>

              <div className="space-y-4">
                <h1 className="max-w-3xl text-4xl text-iron-100 md:text-5xl">
                  Submit A Trading Card Listing
                </h1>
                <p className="max-w-2xl font-body text-lg italic text-iron-400">
                  This submission now creates both the card and its auction listing. Signed-in
                  members can publish with their account identity, while guests can still provide
                  seller details manually alongside pricing and schedule.
                </p>
              </div>
            </div>

            <div className="border border-iron-800 bg-iron-950/70 p-6">
              <FieldLabel className="mb-4">Schema Notes</FieldLabel>
              <div className="space-y-4 text-sm text-iron-300">
                <p>
                  Signed-in members publish through their existing account. Guest submissions still
                  create or reuse a seller profile.
                </p>
                <p>
                  Auction status becomes `open` if the start time has passed, otherwise `pending`.
                </p>
                <p>Creature cards need power and toughness. Land cards skip mana cost.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto grid max-w-6xl gap-10 px-8 py-14 lg:grid-cols-[minmax(0,1fr)_360px]">
        <form className="space-y-8" onSubmit={handleSubmit}>
          <SectionHeader
            title="Seller"
            trailing={currentUser ? 'Linked To Your Account' : 'Required For Guests'}
          />

          <div className="grid gap-6 border border-iron-800 bg-iron-950/70 p-8 md:grid-cols-2">
            {currentUser === undefined ? (
              <StatusBanner tone="info">
                Checking your account details. Seller fields appear for guests only.
              </StatusBanner>
            ) : currentUser ? (
              <>
                <ReadOnlyField label="Seller Name" value={currentUser.name} />
                <ReadOnlyField label="Seller Email" value={currentUser.email} />
              </>
            ) : (
              <>
                <TextField
                  label="Seller Name"
                  value={form.sellerName}
                  placeholder="Rifky Putra"
                  onChange={(value) => updateField('sellerName', value)}
                />
                <TextField
                  label="Seller Email"
                  value={form.sellerEmail}
                  placeholder="rifky@example.com"
                  inputMode="email"
                  onChange={(value) => updateField('sellerEmail', value)}
                />
              </>
            )}
          </div>

          <SectionHeader title="Card Details" trailing="Creates The Card Record" />

          <div className="grid gap-6 border border-iron-800 bg-iron-950/70 p-8 md:grid-cols-2">
            <TextField
              label="Card Name"
              value={form.name}
              placeholder="Ancient Ember Drake"
              onChange={(value) => updateField('name', value)}
            />
            <SelectField
              label="Rarity"
              value={form.rarity}
              onChange={(value) => updateField('rarity', value as Rarity)}
              options={rarities}
            />
            <SelectField
              label="Card Type"
              value={form.cardType}
              onChange={(value) => updateField('cardType', value as CardType)}
              options={cardTypes}
            />
            {showManaCost && (
              <TextField
                label="Mana Cost"
                value={form.manaCost}
                placeholder="5"
                inputMode="numeric"
                onChange={(value) => updateField('manaCost', value)}
              />
            )}
            <TextField
              label="Flavor Text"
              value={form.flavorText}
              placeholder="Its wings leave cinders drifting over the battlefield."
              onChange={(value) => updateField('flavorText', value)}
            />
            <TextAreaField
              label="Description"
              value={form.description}
              placeholder="Describe the card's place in the world, powers, or collectible appeal."
              onChange={(value) => updateField('description', value)}
              className="md:col-span-2"
            />
            {showCreatureStats && (
              <>
                <TextField
                  label="Power"
                  value={form.power}
                  placeholder="6"
                  inputMode="numeric"
                  onChange={(value) => updateField('power', value)}
                />
                <TextField
                  label="Toughness"
                  value={form.toughness}
                  placeholder="4"
                  inputMode="numeric"
                  onChange={(value) => updateField('toughness', value)}
                />
              </>
            )}
          </div>

          <SectionHeader title="Auction Terms" trailing="Creates The Auction Record" />

          <div className="grid gap-6 border border-iron-800 bg-iron-950/70 p-8 md:grid-cols-2">
            <TextField
              label="Starting Price (RP)"
              value={form.startingPrice}
              placeholder="250"
              inputMode="numeric"
              onChange={(value) => updateField('startingPrice', value)}
            />
            <TextField
              label="Reserve Price (RP)"
              value={form.reservePrice}
              placeholder="500"
              inputMode="numeric"
              onChange={(value) => updateField('reservePrice', value)}
            />
            <DateTimeField
              label="Auction Starts At"
              value={form.startsAt}
              onChange={(value) => updateField('startsAt', value)}
            />
            <DateTimeField
              label="Auction Ends At"
              value={form.endsAt}
              onChange={(value) => updateField('endsAt', value)}
            />
          </div>

          <div className="border border-iron-800 bg-iron-950/70 p-8">
            <SectionHeader
              title="Publish"
              trailing="Writes Card + Auction + Seller"
              className="mb-6"
            />
            <div className="space-y-4">
              {clientError && <StatusBanner tone="error">{clientError}</StatusBanner>}
              {state.status === 'error' && (
                <StatusBanner tone="error">{state.message}</StatusBanner>
              )}
              {state.status === 'success' && (
                <StatusBanner tone="success">
                  Listing created successfully.{' '}
                  <Link
                    to="/auction/$auctionId"
                    params={{ auctionId: state.auctionId }}
                    className="font-semibold underline underline-offset-4"
                  >
                    Open the auction
                  </Link>
                </StatusBanner>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-4">
              <AppButton
                type="submit"
                color="primary"
                className="min-w-56 rounded-none px-8 py-4 font-epic text-sm uppercase tracking-[0.28em]"
                isLoading={state.status === 'pending'}
                disabled={isAuthStateLoading}
              >
                Submit Listing
              </AppButton>
              <AppButton
                type="button"
                variant="outline"
                color="default"
                className="min-w-40 rounded-none px-8 py-4 font-epic text-sm uppercase tracking-[0.24em]"
                onClick={() => {
                  setForm(createInitialForm());
                  setClientError(null);
                  reset();
                }}
              >
                Reset Form
              </AppButton>
            </div>
          </div>
        </form>

        <aside className="space-y-8 lg:sticky lg:top-24">
          <SectionHeader title="Listing Preview" />

          <div className="overflow-hidden border border-iron-800 bg-iron-950">
            <div
              className="relative h-80 border-b border-iron-800 bg-[radial-gradient(circle_at_top,_rgba(241,198,41,0.2),_transparent_30%),linear-gradient(135deg,_rgba(163,41,39,0.45),_rgba(19,23,31,0.92))] bg-cover bg-center"
              style={imagePreviewUrl ? { backgroundImage: `url(${imagePreviewUrl})` } : undefined}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-iron-950 via-iron-950/45 to-transparent" />
              <div className="absolute left-5 right-5 top-5 flex items-start justify-between gap-4">
                <RitualBadge>{formatLabel(form.rarity)}</RitualBadge>
                <RitualBadge tone="gold">{formatLabel(form.cardType)}</RitualBadge>
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <FieldLabel className="mb-2 text-iron-300">Preview Name</FieldLabel>
                <h2 className="text-3xl text-iron-50">{form.name.trim() || 'Unnamed Relic'}</h2>
              </div>
            </div>

            <div className="space-y-6 p-6">
              <div className="space-y-3">
                <FieldLabel>Description</FieldLabel>
                <p className="min-h-20 font-body text-base text-iron-300">
                  {form.description.trim() || 'A newly submitted card awaits its first battle.'}
                </p>
              </div>

              {form.flavorText.trim() && (
                <div className="space-y-3">
                  <FieldLabel>Flavor Text</FieldLabel>
                  <p className="font-body text-base italic text-iron-500">
                    {form.flavorText.trim()}
                  </p>
                </div>
              )}

              <IronDivider />

              <div className="grid grid-cols-2 gap-4">
                <PreviewStat
                  label="Starting Bid"
                  value={form.startingPrice.trim() ? `${form.startingPrice.trim()} rp` : '—'}
                />
                <PreviewStat
                  label="Reserve"
                  value={form.reservePrice.trim() ? `${form.reservePrice.trim()} rp` : 'None'}
                />
                <PreviewStat label="Opens" value={startsAtLabel} />
                <PreviewStat label="Closes" value={endsAtLabel} />
              </div>

              <IronDivider />

              <div className="grid grid-cols-2 gap-4">
                <PreviewStat
                  label="Mana Cost"
                  value={showManaCost ? form.manaCost.trim() || '—' : 'Land'}
                />
                <PreviewStat
                  label="Seller"
                  value={currentUser?.name ?? (form.sellerName.trim() || 'Pending')}
                />
                <PreviewStat
                  label="Power"
                  value={showCreatureStats ? form.power.trim() || '—' : 'N/A'}
                />
                <PreviewStat
                  label="Toughness"
                  value={showCreatureStats ? form.toughness.trim() || '—' : 'N/A'}
                />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function buildPayload(form: FormValues, currentUser: SellerIdentity): BuildPayloadResult {
  const sellerName = form.sellerName.trim();
  const sellerEmail = form.sellerEmail.trim();
  const name = form.name.trim();
  const description = form.description.trim();
  const flavorText = form.flavorText.trim();
  const imageUrl = form.imageUrl.trim();

  if (currentUser === null && !sellerName) return { error: 'Seller name is required.' };
  if (currentUser === null && !sellerEmail) return { error: 'Seller email is required.' };
  if (currentUser === null && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sellerEmail)) {
    return { error: 'Seller email must be a valid email address.' };
  }
  if (!name) return { error: 'Card name is required.' };
  if (!description) return { error: 'Card description is required.' };
  if (imageUrl && !isValidUrl(imageUrl)) {
    return { error: 'Image URL must be a valid URL.' };
  }

  const startingPrice = parseRequiredWholeNumber(form.startingPrice, 'Starting price');
  if (typeof startingPrice === 'string') return { error: startingPrice };

  const reservePrice = parseOptionalWholeNumber(form.reservePrice, 'Reserve price');
  if (typeof reservePrice === 'string') return { error: reservePrice };

  const manaCost = parseOptionalWholeNumber(form.manaCost, 'Mana cost');
  if (typeof manaCost === 'string') return { error: manaCost };

  const power = parseOptionalWholeNumber(form.power, 'Power');
  if (typeof power === 'string') return { error: power };

  const toughness = parseOptionalWholeNumber(form.toughness, 'Toughness');
  if (typeof toughness === 'string') return { error: toughness };

  if (form.cardType === 'creature' && (power === undefined || toughness === undefined)) {
    return { error: 'Creature cards require both power and toughness.' };
  }

  const startsAt = parseDateTime(form.startsAt, 'Auction start time');
  if (typeof startsAt === 'string') return { error: startsAt };

  const endsAt = parseDateTime(form.endsAt, 'Auction end time');
  if (typeof endsAt === 'string') return { error: endsAt };

  if (endsAt <= startsAt) {
    return { error: 'Auction end time must be after the start time.' };
  }

  return {
    payload: {
      sellerName: currentUser === null ? sellerName : undefined,
      sellerEmail: currentUser === null ? sellerEmail : undefined,
      name,
      description,
      flavorText: flavorText || undefined,
      rarity: form.rarity,
      cardType: form.cardType,
      manaCost: form.cardType === 'land' ? undefined : manaCost,
      power: form.cardType === 'creature' ? power : undefined,
      toughness: form.cardType === 'creature' ? toughness : undefined,
      imageUrl: imageUrl || undefined,
      startingPrice,
      reservePrice,
      startsAt,
      endsAt,
    },
  };
}

function parseRequiredWholeNumber(value: string, label: string) {
  const trimmed = value.trim();
  if (!trimmed) return `${label} is required.`;
  if (!/^\d+$/.test(trimmed)) return `${label} must be a whole number.`;
  return Number(trimmed);
}

function parseOptionalWholeNumber(value: string, label: string) {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (!/^\d+$/.test(trimmed)) return `${label} must be a whole number.`;
  return Number(trimmed);
}

function parseDateTime(value: string, label: string) {
  if (!value.trim()) return `${label} is required.`;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return `${label} is invalid.`;
  return timestamp;
}

function isValidUrl(value: string) {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function formatLabel(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function toDatetimeLocalValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function formatDatePreview(value: string) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(timestamp);
}


function TextField({
  label,
  value,
  placeholder,
  inputMode,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-3">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="w-full border border-iron-700 bg-iron-900 px-4 py-3 text-sm text-iron-100 transition-colors placeholder:text-iron-600 focus:border-secondary focus:outline-none"
      />
    </label>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-3">
      <FieldLabel>{label}</FieldLabel>
      <div className="w-full border border-iron-700 bg-iron-900/70 px-4 py-3 text-sm text-iron-200">
        {value}
      </div>
    </div>
  );
}

function TextAreaField({
  label,
  value,
  placeholder,
  onChange,
  className = '',
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <label className={`space-y-3 ${className}`}>
      <FieldLabel>{label}</FieldLabel>
      <textarea
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        rows={5}
        className="w-full resize-y border border-iron-700 bg-iron-900 px-4 py-3 text-sm text-iron-100 transition-colors placeholder:text-iron-600 focus:border-secondary focus:outline-none"
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
}) {
  return (
    <label className="space-y-3">
      <FieldLabel>{label}</FieldLabel>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full border border-iron-700 bg-iron-900 px-4 py-3 text-sm text-iron-100 transition-colors focus:border-secondary focus:outline-none"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {formatLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function DateTimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-3">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="datetime-local"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full border border-iron-700 bg-iron-900 px-4 py-3 text-sm text-iron-100 transition-colors focus:border-secondary focus:outline-none"
      />
    </label>
  );
}

function StatusBanner({
  children,
  tone,
}: {
  children: ReactNode;
  tone: 'success' | 'error' | 'info';
}) {
  const styles =
    tone === 'success'
      ? 'border-secondary/40 bg-secondary/10 text-secondary'
      : tone === 'error'
        ? 'border-primary/40 bg-primary/10 text-primary'
        : 'border-iron-700 bg-iron-900/80 text-iron-300';

  return <div className={`border px-4 py-3 text-sm ${styles}`}>{children}</div>;
}

function RitualBadge({
  children,
  tone = 'blood',
}: {
  children: ReactNode;
  tone?: 'blood' | 'gold';
}) {
  const styles =
    tone === 'gold'
      ? 'border-secondary/40 bg-secondary/15 text-secondary'
      : 'border-primary/40 bg-primary/20 text-primary-foreground';

  return (
    <span
      className={`border px-3 py-2 text-[10px] font-bold uppercase tracking-[0.28em] ${styles}`}
    >
      {children}
    </span>
  );
}

function PreviewStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-iron-800 bg-iron-900/80 px-4 py-3">
      <FieldLabel className="mb-2">{label}</FieldLabel>
      <p className="font-epic text-sm text-iron-100">{value}</p>
    </div>
  );
}
